import { useState, useEffect, useCallback } from "react";
import Sidebar from "@/components/Sidebar";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  QrCode, ScanLine, TrendingUp, Crown, PlusCircle, BarChart3, Trash2,
  ExternalLink, Copy, Pencil, FileImage, FileCode, Palette,
  Link2, Phone, MessageCircle, Contact,
} from "lucide-react";
import { useStyledQR } from "@/components/StyledQRCode";
import QRStylePanel from "@/components/QRStylePanel";
import { DEFAULT_STYLE } from "@/lib/presetLogos";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV !== "production" ? "http://localhost:8000" : "");
const API = `${BACKEND_URL}/api`;

function buildStarString(rating, maxStars = 5) {
  const safeMax = Number.isFinite(maxStars) ? Math.max(1, Math.min(10, Math.round(maxStars))) : 5;
  const safeRating = Number.isFinite(rating) ? Math.max(0, Math.min(safeMax, Math.round(rating))) : safeMax;
  return "★".repeat(safeRating) + "☆".repeat(safeMax - safeRating);
}

const qrTypes = [
  { id: "url", label: "URL", icon: Link2, placeholder: "https://example.com" },
  { id: "phone", label: "Phone", icon: Phone, placeholder: "+91 98765 43210" },
  { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, placeholder: "+91 98765 43210" },
  { id: "vcard", label: "vCard", icon: Contact, placeholder: "" },
];

const inputClass = "w-full border border-zinc-200 rounded-md px-4 py-3 text-sm focus:border-[#0047FF] focus:ring-1 focus:ring-[#0047FF] transition-all bg-white outline-none";
const labelClass = "block text-sm font-bold text-zinc-950 mb-2";

function parseVCard(vcardStr) {
  const lines = (vcardStr || "").split("\n");
  const result = { name: "", email: "", phone: "", company: "", title: "" };
  for (const line of lines) {
    if (line.startsWith("FN:")) result.name = line.slice(3).trim();
    else if (line.startsWith("TEL:")) result.phone = line.slice(4).trim();
    else if (line.startsWith("EMAIL:")) result.email = line.slice(6).trim();
    else if (line.startsWith("ORG:")) result.company = line.slice(4).trim();
    else if (line.startsWith("TITLE:")) result.title = line.slice(6).trim();
  }
  return result;
}

function buildVCard(name, phone, email, company, title) {
  return `BEGIN:VCARD\nVERSION:3.0\nFN:${name}\nTEL:${phone}\nEMAIL:${email}\nORG:${company}\nTITLE:${title}\nEND:VCARD`;
}

function EditQRDialog({ qr, open, onOpenChange, onSaved }) {
  const [editType, setEditType] = useState("url");
  const [editContent, setEditContent] = useState("");
  const [editLabel, setEditLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [editTab, setEditTab] = useState("content");
  const [editStyle, setEditStyle] = useState({ ...DEFAULT_STYLE });

  const [vcName, setVcName] = useState("");
  const [vcEmail, setVcEmail] = useState("");
  const [vcPhone, setVcPhone] = useState("");
  const [vcCompany, setVcCompany] = useState("");
  const [vcTitle, setVcTitle] = useState("");

  useEffect(() => {
    if (qr) {
      setEditType(qr.qr_type);
      setEditLabel(qr.label || "");
      setEditStyle(qr.style ? { ...DEFAULT_STYLE, ...qr.style } : { ...DEFAULT_STYLE });
      setEditTab("content");
      if (qr.qr_type === "vcard") {
        const parsed = parseVCard(qr.original_url);
        setVcName(parsed.name);
        setVcEmail(parsed.email);
        setVcPhone(parsed.phone);
        setVcCompany(parsed.company);
        setVcTitle(parsed.title);
        setEditContent(qr.original_url);
      } else {
        setEditContent(qr.original_url);
      }
    }
  }, [qr]);

  const getEditContent = () => {
    if (editType === "vcard") return buildVCard(vcName, vcPhone, vcEmail, vcCompany, vcTitle);
    return editContent;
  };

  const previewUrl = qr?.short_code ? `${BACKEND_URL}/api/r/${qr.short_code}` : "https://qrhub.app";

  const { ref: qrRef, downloadPNG, downloadSVG } = useStyledQR({
    data: previewUrl,
    style: editStyle,
    size: 180,
  });

  const handleSave = async () => {
    const finalContent = getEditContent();
    if (!finalContent.trim()) {
      toast.error("Content cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const stylePayload = { ...editStyle };
      if (stylePayload.logo_preset) stylePayload.logo_base64 = null;

      const res = await fetch(`${API}/qr/${qr.qr_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          qr_type: editType,
          content: finalContent,
          label: editLabel || null,
          style: stylePayload,
        }),
      });
      if (res.ok) {
        toast.success("QR code updated");
        onSaved();
        onOpenChange(false);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to update");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  if (!qr) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-qr-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
            Edit QR Code
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-5 mt-2">
          {/* Left: Content + Style tabs */}
          <div className="md:col-span-3">
            <Tabs value={editTab} onValueChange={setEditTab}>
              <TabsList className="w-full grid grid-cols-2 bg-zinc-100 rounded-lg p-1 mb-4">
                <TabsTrigger value="content" className="text-xs font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md flex items-center gap-1.5">
                  <Link2 className="h-3 w-3" /> Content
                </TabsTrigger>
                <TabsTrigger value="style" className="text-xs font-bold data-[state=active]:bg-white data-[state=active]:shadow-sm rounded-md flex items-center gap-1.5" data-testid="edit-style-tab">
                  <Palette className="h-3 w-3" /> Style
                </TabsTrigger>
              </TabsList>

              <TabsContent value="content" className="space-y-4">
                {/* QR Type */}
                <Tabs value={editType} onValueChange={(v) => { setEditType(v); if (v !== "vcard") setEditContent(""); }}>
                  <TabsList className="w-full grid grid-cols-4 bg-zinc-100 rounded-lg p-1" data-testid="edit-qr-type-tabs">
                    {qrTypes.map((t) => (
                      <TabsTrigger
                        key={t.id}
                        value={t.id}
                        data-testid={`edit-qr-type-${t.id}`}
                        className="text-[10px] font-bold data-[state=active]:bg-white data-[state=active]:text-zinc-950 data-[state=active]:shadow-sm rounded-md flex items-center gap-1"
                      >
                        <t.icon className="h-3 w-3" />
                        {t.label}
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {qrTypes.filter((t) => t.id !== "vcard").map((t) => (
                    <TabsContent key={t.id} value={t.id} className="mt-4">
                      <label className={labelClass}>
                        {t.id === "url" ? "Destination URL" : t.id === "phone" ? "Phone Number" : "WhatsApp Number"}
                      </label>
                      <input
                        data-testid={`edit-input-${t.id}`}
                        type="text"
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        placeholder={t.placeholder}
                        className={inputClass}
                      />
                    </TabsContent>
                  ))}

                  <TabsContent value="vcard" className="mt-4 space-y-3">
                    <div>
                      <label className={labelClass}>Full Name</label>
                      <input data-testid="edit-input-vcard-name" type="text" value={vcName} onChange={(e) => setVcName(e.target.value)} placeholder="John Doe" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Email</label>
                      <input data-testid="edit-input-vcard-email" type="email" value={vcEmail} onChange={(e) => setVcEmail(e.target.value)} placeholder="john@co.com" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Phone</label>
                      <input data-testid="edit-input-vcard-phone" type="text" value={vcPhone} onChange={(e) => setVcPhone(e.target.value)} placeholder="+91 98765 43210" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Company</label>
                      <input data-testid="edit-input-vcard-company" type="text" value={vcCompany} onChange={(e) => setVcCompany(e.target.value)} placeholder="Acme Inc" className={inputClass} />
                    </div>
                    <div>
                      <label className={labelClass}>Job Title</label>
                      <input data-testid="edit-input-vcard-title" type="text" value={vcTitle} onChange={(e) => setVcTitle(e.target.value)} placeholder="CEO" className={inputClass} />
                    </div>
                  </TabsContent>
                </Tabs>

                <div>
                  <label className={labelClass}>Label</label>
                  <input
                    data-testid="edit-input-label"
                    type="text"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    placeholder="e.g. Business Card"
                    className={inputClass}
                  />
                </div>
              </TabsContent>

              <TabsContent value="style">
                <QRStylePanel style={editStyle} onChange={setEditStyle} />
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: Preview */}
          <div className="md:col-span-2 flex flex-col items-center">
            <div
              className="rounded-2xl p-4 mb-4 border-2 border-zinc-100"
              style={{ backgroundColor: editStyle.bg_color || "#FFFFFF" }}
              data-testid="edit-qr-preview"
            >
              <div className="flex flex-col items-center">
                <div ref={qrRef} />
                {editStyle.show_review_stars && (
                  <div
                    className="mt-3 text-xs font-black tracking-wide"
                    style={{ color: editStyle.fg_color || "#09090B" }}
                    data-testid="edit-qr-preview-stars"
                  >
                    {buildStarString(editStyle.review_rating, 5)}
                  </div>
                )}
                {(editStyle.bottom_text || "").trim() && (
                  <div
                    className="mt-2 text-[10px] font-bold text-center max-w-[180px] break-words"
                    style={{ color: editStyle.fg_color || "#09090B" }}
                    data-testid="edit-qr-preview-bottom-text"
                  >
                    {editStyle.bottom_text}
                  </div>
                )}
              </div>
            </div>
            <code className="text-[9px] text-zinc-400 font-mono mb-3 text-center break-all px-2">
              {previewUrl}
            </code>
            <div className="flex gap-2 w-full">
              <Button
                data-testid="edit-download-png"
                variant="outline"
                size="sm"
                className="flex-1 font-bold text-xs"
                onClick={() => downloadPNG(`qr-${qr.short_code}`)}
              >
                <FileImage className="mr-1 h-3.5 w-3.5" />
                PNG
              </Button>
              <Button
                data-testid="edit-download-svg"
                variant="outline"
                size="sm"
                className="flex-1 font-bold text-xs"
                onClick={() => downloadSVG(`qr-${qr.short_code}`)}
              >
                <FileCode className="mr-1 h-3.5 w-3.5" />
                SVG
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" className="font-bold" onClick={() => onOpenChange(false)} data-testid="edit-cancel-button">
            Cancel
          </Button>
          <Button
            data-testid="edit-save-button"
            onClick={handleSave}
            disabled={saving}
            className="bg-[#0047FF] text-white hover:bg-[#0033CC] font-bold active:scale-[0.98]"
          >
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [qrCodes, setQrCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingQR, setEditingQR] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [dashRes, qrRes] = await Promise.all([
        fetch(`${API}/dashboard`, { credentials: "include" }),
        fetch(`${API}/qr/list`, { credentials: "include" }),
      ]);
      if (dashRes.ok) setStats(await dashRes.json());
      if (qrRes.ok) setQrCodes(await qrRes.json());
    } catch (err) {
      console.error("Failed to fetch dashboard data", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (qrId) => {
    try {
      const res = await fetch(`${API}/qr/${qrId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("QR code deleted");
        fetchData();
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setDeleteTarget(null);
    }
  };

  const copyLink = (shortCode) => {
    navigator.clipboard.writeText(`${BACKEND_URL}/api/r/${shortCode}`);
    toast.success("Link copied to clipboard");
  };

  const openEdit = (qr) => {
    setEditingQR(qr);
    setEditOpen(true);
  };

  const statCards = stats
    ? [
        { label: "Total QR Codes", value: stats.total_qr, icon: QrCode, color: "#0047FF" },
        { label: "Total Scans", value: stats.total_scans, icon: ScanLine, color: "#09090B" },
        { label: "Today's Scans", value: stats.today_scans, icon: TrendingUp, color: "#10B981" },
        {
          label: "Plan",
          value: stats.plan === "free" ? `Free (${stats.trial_days_left}d left)` : stats.plan.charAt(0).toUpperCase() + stats.plan.slice(1),
          icon: Crown,
          color: "#F59E0B",
        },
      ]
    : [];

  if (loading) {
    return (
      <div className="flex h-screen bg-zinc-50">
        <Sidebar />
        <main className="flex-1 flex items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-zinc-200 border-t-[#0047FF]" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-zinc-50" data-testid="dashboard-page">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <Toaster richColors position="top-right" />
        <div className="p-6 lg:p-8">
          <div className="mb-8 flex items-center justify-between pl-12 lg:pl-0">
            <div>
              <h1
                className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950"
                style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                data-testid="dashboard-greeting"
              >
                Welcome back, {user?.name?.split(" ")[0]}
              </h1>
              <p className="mt-1 text-sm text-zinc-500">Here's your QR code overview</p>
            </div>
            <Button
              data-testid="create-qr-button"
              onClick={() => navigate("/create")}
              className="bg-[#0047FF] text-white hover:bg-[#0033CC] font-bold rounded-lg active:scale-[0.98] transition-all"
            >
              <PlusCircle className="mr-2 h-4 w-4" />
              New QR Code
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {statCards.map((s, i) => (
              <motion.div
                key={s.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
              >
                <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300" data-testid={`stat-card-${s.label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-400 mb-1">{s.label}</p>
                        <p className="text-2xl font-black text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{s.value}</p>
                      </div>
                      <div className="flex h-10 w-10 items-center justify-center rounded-lg" style={{ backgroundColor: s.color + "12" }}>
                        <s.icon className="h-5 w-5" style={{ color: s.color }} strokeWidth={1.5} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>

          <Card className="border-zinc-200 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold text-zinc-950" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Your QR Codes
                </CardTitle>
                <Badge variant="secondary" className="text-xs font-bold" data-testid="qr-count-badge">
                  {qrCodes.length} total
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {qrCodes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <QrCode className="h-12 w-12 text-zinc-200 mb-4" strokeWidth={1} />
                  <p className="text-sm font-bold text-zinc-500 mb-1">No QR codes yet</p>
                  <p className="text-xs text-zinc-400 mb-6">Create your first QR code to get started</p>
                  <Button
                    data-testid="empty-create-qr-button"
                    onClick={() => navigate("/create")}
                    className="bg-[#0047FF] text-white hover:bg-[#0033CC] font-bold rounded-lg active:scale-[0.98]"
                  >
                    Create QR Code
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-zinc-200 bg-zinc-50">
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Label</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Type</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Short Code</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Scans</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4">Created</TableHead>
                        <TableHead className="text-xs uppercase tracking-[0.1em] text-zinc-500 font-bold py-3 px-4 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {qrCodes.map((qr) => (
                        <TableRow key={qr.qr_id} className="border-zinc-100 hover:bg-zinc-50 transition-colors" data-testid={`qr-row-${qr.short_code}`}>
                          <TableCell className="py-4 px-4 text-sm font-medium text-zinc-950">{qr.label}</TableCell>
                          <TableCell className="py-4 px-4">
                            <Badge variant="outline" className="text-xs font-bold uppercase tracking-wider">{qr.qr_type}</Badge>
                          </TableCell>
                          <TableCell className="py-4 px-4">
                            <code className="text-xs bg-zinc-100 px-2 py-1 rounded font-mono text-zinc-600">{qr.short_code}</code>
                          </TableCell>
                          <TableCell className="py-4 px-4 text-sm font-bold text-zinc-950">{qr.scan_count}</TableCell>
                          <TableCell className="py-4 px-4 text-sm text-zinc-500">{new Date(qr.created_at).toLocaleDateString()}</TableCell>
                          <TableCell className="py-4 px-4 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <button
                                data-testid={`edit-qr-${qr.short_code}`}
                                onClick={() => openEdit(qr)}
                                className="rounded-md p-2 text-zinc-400 hover:bg-[#0047FF]/10 hover:text-[#0047FF] transition-colors"
                                title="Edit QR"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                              <button
                                data-testid={`copy-link-${qr.short_code}`}
                                onClick={() => copyLink(qr.short_code)}
                                className="rounded-md p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
                                title="Copy link"
                              >
                                <Copy className="h-4 w-4" />
                              </button>
                              <button
                                data-testid={`view-analytics-${qr.short_code}`}
                                onClick={() => navigate(`/analytics/${qr.qr_id}`)}
                                className="rounded-md p-2 text-zinc-400 hover:bg-zinc-100 hover:text-[#0047FF] transition-colors"
                                title="View analytics"
                              >
                                <BarChart3 className="h-4 w-4" />
                              </button>
                              <a
                                href={`${BACKEND_URL}/api/r/${qr.short_code}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="rounded-md p-2 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 transition-colors"
                                title="Open link"
                                data-testid={`open-link-${qr.short_code}`}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                              <button
                                data-testid={`delete-qr-${qr.short_code}`}
                                onClick={() => setDeleteTarget(qr)}
                                className="rounded-md p-2 text-zinc-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                                title="Delete"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      <EditQRDialog qr={editingQR} open={editOpen} onOpenChange={setEditOpen} onSaved={fetchData} />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent data-testid="delete-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Delete QR Code
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-zinc-500">
              Are you sure you want to delete <strong className="text-zinc-950">{deleteTarget?.label}</strong>?
              This will permanently remove the QR code and all associated scan analytics. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-bold" data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              onClick={() => handleDelete(deleteTarget?.qr_id)}
              className="bg-red-500 text-white hover:bg-red-600 font-bold"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
